import { IconHouseBed16 } from '@warp-ds/icons/react';
import { Button } from '@warp-ds/react/components/button';

import { useConfig } from 'shared/config/ConfigContext';
import { formatDistance, thousandSeparated } from 'shared/util/formatting';

import type { Filters, Offer } from '@/interfaces';
import { getNumberOfPax, getPricePerPerson, getPriceTotal } from '@/lib/utils/hotelResultItemUtil';
import { addHotelPageFiltersToUrl, browserQueryParamFor } from '@/lib/utils/queryParamFilterConversions';
import { trackClickOfferResult, trackSeeMoreOffers } from '@/lib/utils/tracking';
import displayValues from '@/resources/displayValues.json';
import { SupplierLogo } from '../images/SupplierLogo';
import { FlightDateAndTime } from './FlightDateAndTime';
import { StarRating } from './StarRating';

export interface ResultListItemProps {
    offer: Offer;
    filters: Filters;
    paxEnabled?: boolean;
}

export const ResultListItem = ({ offer, filters, paxEnabled }: ResultListItemProps) => {
    const { contextPathPP, imgPath } = useConfig();

    const numPax = getNumberOfPax(offer.numberOfAdults, offer.numberOfChildren);
    const pricePerPerson = thousandSeparated(getPricePerPerson(offer.pricePerPerson, numPax, paxEnabled, offer.priceTotal));
    const priceTotal = thousandSeparated(getPriceTotal(offer.pricePerPerson, numPax, paxEnabled, offer.priceTotal));

    const hotelUrl = addHotelPageFiltersToUrl(
        `${contextPathPP}/hotell?${browserQueryParamFor('hotelId', offer.hotelId)}`,
        filters,
        paxEnabled,
    );

    const joinLocation = (...parts: Array<string | null | undefined>) =>
        parts
            .map((s) => s?.trim())
            .filter((s): s is string => s && s.length > 0)
            .join(', ');

    const firstImage = `${imgPath}1280w${offer.imagePaths[0]}`;

    return (
        <article
            className="flex flex-col border s-border rounded-8 md:flex-row items-stretch"
            data-testid={`resultListItem-${offer.offerId}`}
            data-result-item
            tabIndex={-1}>
            <div className="md:w-1/2 w-full md:max-w-[400px] md:max-h-[220px] overflow-hidden rounded-tl-8 rounded-tr-8 md:rounded-tr-0 md:rounded-bl-8">
                <img src={firstImage} alt={offer.hotelName ?? 'Hotellbilde'} className="object-cover w-full h-full" loading="lazy" />
            </div>
            <div className="flex flex-col p-16 w-full">
                <header className="flex flex-col gap-8 md:flex-row-reverse md:justify-between ">
                    <div className="flex flex-row items-center gap-8 justify-between md:flex-col-reverse md:gap-16 md:items-end md:h-fit">
                        <StarRating id={`rating-${offer.offerId}`} rating={offer.rating} emptyStars />
                        <SupplierLogo supplier={offer.supplier} />
                    </div>
                    <div className="flex flex-col gap-8">
                        <h2 className="t4 mb-0" data-testid="offerHotelName" id={`resultListItem-${offer.offerId}`}>
                            {offer.hotelName}
                        </h2>
                        <p className="s-text-subtle text-xs mb-0" data-testid="offerDestination">
                            {joinLocation(offer.destination, offer.region, offer.country)}
                        </p>
                        <FlightDateAndTime departureDate={offer.departureDate} returnDate={offer.returnDate} />
                    </div>
                </header>

                <p className="flex flex-row items-center gap-4 text-detail font-bold" data-testid="offerDuration">
                    <IconHouseBed16 aria-hidden="true" /> {`${offer.duration} dager`}
                    {offer.board && offer.board !== 'no_meals' && (
                        <span className="font-normal" data-testid="offerMeal">
                            • {displayValues.amenities.meals[offer.board]}
                        </span>
                    )}
                </p>

                <div className="flex flex-col gap-8 md:flex-row md:justify-between md:items-end">
                    <p className="s-text-subtle text-detail" data-testid="offerDistance">
                        {formatDistance(offer.distanceToBeach)} fra strand • {formatDistance(offer.distanceToCity)} fra sentrum
                    </p>
                    <p className="flex t5 md:t6">{`${priceTotal} kr/${numPax} personer`}</p>
                </div>

                <footer className="flex w-full items-stretch gap-4 md:items-center md:justify-between">
                    <Button
                        className="text-xs sm:text-s flex-[2_1_0%] min-w-[14rem] max-w-none md:flex-none md:basis-auto md:shrink-0 md:w-auto md:min-w-0 md:border-none md:px-0 hover:!bg-transparent hover:!underline"
                        secondary
                        href={hotelUrl}
                        onClick={() => trackSeeMoreOffers(offer.offerId, offer.hotelName)}
                        rel="nofollow"
                        data-testid={`resultDetailLink-${offer.hotelId}`}>
                        Se flere reisealternativer
                        <span className="sr-only"> for {offer.hotelName}</span>
                    </Button>
                    <Button
                        primary
                        className="text-s flex-[1_1_0%] min-w-0 max-w-none md:flex-none md:basis-auto md:shrink-0 md:w-auto"
                        href={`${contextPathPP}/ut?offerId=${offer.offerId}&fromPage=result${filters.agesOfChildren?.length ? `&agesOfChildren=${filters.agesOfChildren.join(',')}` : ''}`}
                        onClick={() => trackClickOfferResult(offer)}
                        data-testid="offerLink"
                        rel="nofollow noopener"
                        target="_blank">
                        {pricePerPerson} kr
                    </Button>
                </footer>
            </div>
        </article>
    );
};
